import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Flame,
  Heart,
  RotateCcw,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { AssessmentPriority, AssessmentResult, AssessmentResultItem } from '../../types';
import { PRIORITY_COLORS } from './AssessmentCharts';
import { HAIRLINE, Panel, PanelDivider } from './AssessmentUI';

const fa = (n: number) => n.toLocaleString('fa-IR');

/**
 * صفحهٔ نتیجهٔ پرسشنامه‌های حالت «تحلیل» (resultMode = ANALYSIS).
 *
 * برخلاف مسیر رشد که «امتیاز کمتر = دورهٔ فوری‌تر» را نشان می‌دهد، اینجا هر
 * محور با **شدت** (۰ تا ۱۰۰٪) و یک متن تحلیلِ سطح‌بندی‌شده نمایش داده می‌شود
 * و صفحه با یک فراخوان (CTA) که ادمین برای پرسشنامه تعریف کرده تمام می‌شود.
 *
 * رنگ سطح‌ها همان پالت وضعیت مسیر رشد است تا زبان بصری سایت یکدست بماند؛
 * فقط برچسب‌ها فرق می‌کند (فعال / نیمه‌فعال / آرام) چون اینجا «اولویت دوره»
 * معنا ندارد.
 */

const LEVEL_LABELS: Record<AssessmentPriority, string> = {
  HIGH: 'فعال',
  MEDIUM: 'نیمه‌فعال',
  LOW: 'آرام',
};

const LEVEL_CHIP: Record<AssessmentPriority, string> = {
  HIGH: 'border-[#F3C9C9] bg-[#FDF2F2] text-[#8E2020]',
  MEDIUM: 'border-[#F4DFAE] bg-[#FEF8EA] text-[#7A5405]',
  LOW: 'border-[#BFE6BF] bg-[#F1FAF1] text-[#0A6B0A]',
};

const LevelChip: React.FC<{ level: AssessmentPriority; className?: string }> = ({
  level,
  className = '',
}) => (
  <span
    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black ${LEVEL_CHIP[level]} ${className}`}
  >
    <span
      aria-hidden
      className="h-2 w-2 rounded-full"
      style={{ backgroundColor: PRIORITY_COLORS[level] }}
    />
    {LEVEL_LABELS[level]}
  </span>
);

/** حلقهٔ درصد شدت برای کارت الگوی غالب */
const IntensityRing: React.FC<{ value: number; color: string; size?: number }> = ({
  value,
  color,
  size = 96,
}) => {
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#EEF2F7"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped / 100)}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-black tabular-nums text-slate-900">{fa(clamped)}٪</span>
        <span className="text-[9.5px] font-bold text-slate-400">شدت</span>
      </div>
    </div>
  );
};

/** جملهٔ جمع‌بندی بر اساس تعداد محورهای هر سطح — مستقل از محتوای پرسشنامه */
function overallNarrative(high: number, medium: number, total: number): string {
  if (!total) return '';
  if (high >= 3) {
    return 'چند محور به‌صورت هم‌زمان فعال‌اند و احتمالاً یکدیگر را تقویت می‌کنند. این ترکیب معمولاً پشت تله‌های تکراری زندگی است و کار روی ریشهٔ مشترکشان، بیشترین تغییر را می‌سازد.';
  }
  if (high >= 1) {
    return 'یک یا دو محور اصلی فعال است و بقیه در سایهٔ آن قرار دارند. تمرکز روی همین ریشه، معمولاً بقیهٔ محورها را هم آرام می‌کند.';
  }
  if (medium >= 1) {
    return 'هیچ محوری در سطح شدید نیست، اما چند محور نیمه‌فعال دارید که در فشار، بحران یا رابطه‌های نزدیک بیدار می‌شوند. این بهترین زمان برای پیشگیری است.';
  }
  return 'همهٔ محورها آرام‌اند و تصویر درونی نسبتاً سالمی دارید. محورهایی که شدت بیشتری دارند را برای پیشگیری دنبال کنید.';
}

/**
 * دکمهٔ فراخوان. آدرس داخلی با Link (بدون رفرش) و آدرس کامل با <a> باز می‌شود.
 * `light` روی پس‌زمینهٔ گرادیان، `brand` روی پس‌زمینهٔ سفید صفحه.
 */
const CtaButton: React.FC<{
  url: string;
  label: string;
  variant?: 'light' | 'brand';
  className?: string;
}> = ({ url, label, variant = 'light', className = '' }) => {
  const external = /^https?:\/\//i.test(url);
  const cls = `group inline-flex items-center justify-center gap-2 rounded-2xl px-7 py-3.5 text-sm font-black transition hover:-translate-y-0.5 ${
    variant === 'light'
      ? 'bg-white text-[#16307F] shadow-[0_18px_34px_-18px_rgba(0,0,0,0.5)]'
      : 'text-white shadow-[0_18px_34px_-18px_rgba(28,59,155,0.9)]'
  } ${className}`;
  const style =
    variant === 'brand' ? { background: 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' } : undefined;
  const inner = (
    <>
      {label}
      <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
    </>
  );
  return external ? (
    <a href={url} className={cls} style={style}>
      {inner}
    </a>
  ) : (
    <Link to={url} className={cls} style={style}>
      {inner}
    </Link>
  );
};

interface Props {
  result: AssessmentResult;
  allowRetake: boolean;
  onRetake: () => void;
}

export const AnalysisResult: React.FC<Props> = ({ result, allowRetake, onRetake }) => {
  // مرتب بر اساس شدت (بیشترین اول)؛ رتبهٔ سرور همین ترتیب را می‌دهد ولی صریح می‌کنیم
  const items = useMemo(
    () => [...result.items].sort((a, b) => b.intensity - a.intensity || a.rank - b.rank),
    [result.items],
  );
  const dominant = items[0];
  /**
   * پرسشنامهٔ تک‌محوری (مثل تست سحر روانی): جمع‌بندی چندمحوری و نمودار مقایسه
   * بی‌معناست؛ فقط امتیاز، سطح و متن تحلیل همان محور نمایش داده می‌شود.
   */
  const single = items.length === 1;
  const counts = useMemo(
    () => ({
      high: items.filter((i) => i.priority === 'HIGH').length,
      medium: items.filter((i) => i.priority === 'MEDIUM').length,
      low: items.filter((i) => i.priority === 'LOW').length,
    }),
    [items],
  );
  const averageIntensity =
    result.summary.averageIntensity ??
    (items.length ? Math.round(items.reduce((s, i) => s + i.intensity, 0) / items.length) : 0);

  // اولین کارت تحلیل باز است تا کاربر بفهمد کارت‌ها بازشدنی‌اند
  const [openId, setOpenId] = useState<string>(items[1]?.sectionId ?? '');
  const detailItems = items.slice(1);

  const cta = result.cta;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="space-y-4"
    >
      {/* ── هیرو + خلاصهٔ عددی ─────────────────────────────────────────── */}
      <Panel className="overflow-hidden">
        <div
          className="relative px-5 py-8 text-center text-white sm:px-8"
          style={{ background: 'linear-gradient(140deg,#16307F 0%,#1C3B9B 50%,#4B2FA8 100%)' }}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-70"
            style={{
              background:
                'radial-gradient(420px 200px at 82% 0%, rgba(255,255,255,0.20), transparent 65%)',
            }}
          />
          <div className="relative">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-white/25 bg-white/12 backdrop-blur-sm">
              <Sparkles className="h-7 w-7" />
            </span>
            <h1 className="mt-3 text-2xl font-black sm:text-[28px]">تحلیل اختصاصی شما آماده است</h1>
            <p className="mx-auto mt-2 max-w-xl text-[13px] leading-8 text-white/85">
              {single
                ? `${result.assessment.title} — نتیجه بر اساس پاسخ‌های شما محاسبه و تحلیل سطح شما در ادامه آمده است.`
                : `${result.assessment.title} — هر محور با درصد شدت و یک تحلیل سطح‌بندی‌شده. هرچه شدت بیشتر باشد، آن محور نقش پررنگ‌تری در الگوهای تکراری زندگی شما دارد.`}
            </p>
          </div>
        </div>
        {single && dominant ? (
          <div className="grid grid-cols-2 gap-2.5 p-4 sm:p-5">
            <div className="rounded-2xl border border-[#E6ECFA] bg-white px-3 py-3 text-center">
              <p className="text-[10px] font-bold text-slate-500">
                {dominant.agreeCount != null ? 'پاسخ «بله»' : 'امتیاز شما'}
              </p>
              <p className="mt-1 text-lg font-black text-slate-900">
                {dominant.agreeCount != null
                  ? `${fa(dominant.agreeCount)} از ${fa(dominant.questionCount ?? 0)}`
                  : `${fa(dominant.score25)} / ${fa(25)}`}
              </p>
            </div>
            <div
              className={`rounded-2xl border px-3 py-3 text-center ${LEVEL_CHIP[dominant.priority]}`}
            >
              <p className="text-[10px] font-bold">سطح</p>
              <p className="mt-1 text-lg font-black">{LEVEL_LABELS[dominant.priority]}</p>
            </div>
          </div>
        ) : (
        <div className="grid grid-cols-2 gap-2.5 p-4 sm:grid-cols-4 sm:p-5">
          <div className="rounded-2xl border border-[#F3C9C9] bg-[#FDF2F2] px-3 py-3 text-center">
            <p className="text-[10px] font-bold text-[#8E2020]">محور فعال</p>
            <p className="mt-1 text-lg font-black text-[#8E2020]">{fa(counts.high)}</p>
          </div>
          <div className="rounded-2xl border border-[#F4DFAE] bg-[#FEF8EA] px-3 py-3 text-center">
            <p className="text-[10px] font-bold text-[#7A5405]">نیمه‌فعال</p>
            <p className="mt-1 text-lg font-black text-[#7A5405]">{fa(counts.medium)}</p>
          </div>
          <div className="rounded-2xl border border-[#BFE6BF] bg-[#F1FAF1] px-3 py-3 text-center">
            <p className="text-[10px] font-bold text-[#0A6B0A]">آرام</p>
            <p className="mt-1 text-lg font-black text-[#0A6B0A]">{fa(counts.low)}</p>
          </div>
          <div className="rounded-2xl border border-[#E6ECFA] bg-white px-3 py-3 text-center">
            <p className="text-[10px] font-bold text-slate-500">میانگین شدت</p>
            <p className="mt-1 text-lg font-black text-slate-900">{fa(averageIntensity)}٪</p>
          </div>
        </div>
        )}
      </Panel>

      {/* ── الگوی غالب ─────────────────────────────────────────────────── */}
      {dominant ? (
        <Panel className="overflow-hidden">
          <div className="flex items-center gap-2 px-5 pt-5">
            <Flame className="h-4 w-4 text-[#d03b3b]" />
            <p className="text-[14px] font-black text-slate-900">
              {single ? 'نتیجهٔ شما' : 'الگوی غالب شما'}
            </p>
          </div>
          <div className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-start">
            <IntensityRing value={dominant.intensity} color={PRIORITY_COLORS[dominant.priority]} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-black text-slate-900">{dominant.title}</h2>
                <LevelChip level={dominant.priority} />
              </div>
              <p className="mt-2 text-[12.5px] font-bold leading-7 text-slate-500">
                {single
                  ? dominant.agreeCount != null
                    ? `شما به ${fa(dominant.agreeCount)} سوال از ${fa(dominant.questionCount ?? 0)} سوال پاسخ «بله» داده‌اید.`
                    : `امتیاز شما ${fa(dominant.score25)} از ${fa(25)} است.`
                  : overallNarrative(counts.high, counts.medium, items.length)}
              </p>
            </div>
          </div>
          {dominant.analysis ? (
            <>
              <PanelDivider />
              <p className="px-5 py-4 text-[13.5px] leading-8 text-slate-700">{dominant.analysis}</p>
            </>
          ) : null}
        </Panel>
      ) : null}

      {/* ── نمودار شدت همهٔ محورها (فقط چندمحوری) ─────────────────────── */}
      {!single ? (
      <Panel className="p-4 sm:p-6">
        <p className="text-[15px] font-black text-slate-900">نقشهٔ شدت محورها</p>
        <p className="mt-1 max-w-2xl text-[11.5px] font-bold leading-7 text-slate-500">
          درصد شدت هر محور از پاسخ‌های شما محاسبه شده است. ۱۰۰٪ یعنی همهٔ جمله‌های آن محور
          کاملاً با شما هم‌خوان بوده‌اند.
        </p>
        <div className="mt-4 space-y-2">
          {items.map((item) => {
            const color = PRIORITY_COLORS[item.priority];
            return (
              <div
                key={item.sectionId}
                className="rounded-2xl border bg-white px-3 py-2.5"
                style={{ borderColor: HAIRLINE }}
              >
                <div className="flex items-center gap-2.5">
                  <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-slate-800">
                    {item.title}
                  </span>
                  <LevelChip level={item.priority} className="hidden sm:inline-flex" />
                  <span className="shrink-0 text-[13px] font-black tabular-nums text-slate-900">
                    {fa(item.intensity)}٪
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div
                    className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#EEF2F7]"
                    role="img"
                    aria-label={`${item.title}: شدت ${fa(item.intensity)} درصد — ${LEVEL_LABELS[item.priority]}`}
                  >
                    <div
                      className="h-full rounded-full transition-[width] duration-700 ease-out"
                      style={{ width: `${Math.max(3, item.intensity)}%`, backgroundColor: color }}
                    />
                  </div>
                  <LevelChip level={item.priority} className="sm:hidden" />
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[10.5px] font-bold text-slate-400">
          {(['HIGH', 'MEDIUM', 'LOW'] as AssessmentPriority[]).map((level) => (
            <span key={level} className="inline-flex items-center gap-1.5">
              <span
                aria-hidden
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: PRIORITY_COLORS[level] }}
              />
              {LEVEL_LABELS[level]}
            </span>
          ))}
        </div>
      </Panel>
      ) : null}

      {/* ── CTA ───────────────────────────────────────────────────────── */}
      {cta ? (
        <Panel className="overflow-hidden">
          <div
            className="relative px-5 py-8 text-white sm:px-8"
            style={{ background: 'linear-gradient(135deg,#4B2FA8 0%,#1C3B9B 55%,#16307F 100%)' }}
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-60"
              style={{
                background:
                  'radial-gradient(520px 220px at 10% 0%, rgba(255,255,255,0.18), transparent 65%)',
              }}
            />
            <div className="relative flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/25 bg-white/12 backdrop-blur-sm">
                <Heart className="h-6 w-6" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-black text-white/70">قدم بعدی شما</p>
                {cta.title ? (
                  <h3 className="mt-1 text-xl font-black leading-9 sm:text-[22px]">{cta.title}</h3>
                ) : null}
                {cta.text ? (
                  <p className="mt-2 max-w-2xl text-[13px] leading-8 text-white/88">{cta.text}</p>
                ) : null}
              </div>
              <CtaButton
                url={cta.url}
                label={cta.label || 'اطلاعات بیشتر'}
                className="w-full sm:w-auto"
              />
            </div>
          </div>
        </Panel>
      ) : null}

      {/* ── تحلیل تفصیلی سایر محورها ────────────────────────────────────── */}
      {detailItems.length ? (
        <Panel className="overflow-hidden">
          <div className="flex items-center gap-2 px-5 pt-5">
            <ShieldCheck className="h-4 w-4 text-[#1C3B9B]" />
            <p className="text-[14px] font-black text-slate-900">تحلیل سایر محورها</p>
          </div>
          <p className="px-5 pb-3 pt-1 text-[11.5px] font-bold leading-7 text-slate-500">
            به ترتیب شدت. روی هر محور بزنید تا تحلیل آن باز شود.
          </p>
          <PanelDivider />
          <div>
            {detailItems.map((item: AssessmentResultItem, index) => {
              const open = openId === item.sectionId;
              return (
                <div key={item.sectionId}>
                  {index > 0 ? <PanelDivider /> : null}
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? '' : item.sectionId)}
                    aria-expanded={open}
                    className="flex w-full items-center gap-3 px-5 py-4 text-right transition hover:bg-[#FAFBFE]"
                  >
                    <span
                      className="flex h-9 w-12 shrink-0 items-center justify-center rounded-xl text-[12px] font-black tabular-nums"
                      style={{
                        backgroundColor: `${PRIORITY_COLORS[item.priority]}14`,
                        color: PRIORITY_COLORS[item.priority],
                      }}
                    >
                      {fa(item.intensity)}٪
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-black text-slate-900">
                        {item.title}
                      </span>
                    </span>
                    <LevelChip level={item.priority} />
                    {open ? (
                      <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                    )}
                  </button>
                  {open ? (
                    <p className="px-5 pb-5 text-[13px] leading-8 text-slate-700">
                      {item.analysis || 'برای این سطح هنوز تحلیلی ثبت نشده است.'}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </Panel>
      ) : null}

      <p className="px-2 text-center text-[11px] font-bold leading-6 text-slate-400">
        این تحلیل یک ابزار خودشناسی است و جایگزین تشخیص یا درمان تخصصی نیست.
      </p>

      <div className="flex flex-col gap-2.5 sm:flex-row">
        {cta ? (
          <CtaButton
            url={cta.url}
            label={cta.label || 'اطلاعات بیشتر'}
            variant="brand"
            className="flex-1"
          />
        ) : (
          <Link
            to="/dashboard"
            className="flex-1 rounded-2xl px-4 py-3.5 text-center text-sm font-black text-white"
            style={{ background: 'linear-gradient(135deg,#1C3B9B,#3A2FA0)' }}
          >
            بازگشت به داشبورد
          </Link>
        )}
        {allowRetake ? (
          <button
            type="button"
            onClick={onRetake}
            className="flex items-center justify-center gap-1.5 rounded-2xl border border-[#C8D6FF] bg-white px-5 py-3.5 text-sm font-black text-[#1C3B9B] transition hover:bg-[#F4F7FF]"
          >
            <RotateCcw className="h-4 w-4" />
            پاسخ‌دهی مجدد
          </button>
        ) : null}
      </div>
    </motion.div>
  );
};

export default AnalysisResult;
